import { resolveCart } from '../../../cart/cartApi'
import {
  readCheckoutGuestCartId,
  readStoredCartId,
  writeCheckoutGuestCartId,
  writeStoredCartId,
} from '../../../cart/cartPersistence'
import {
  CheckoutApiError,
  createCheckoutSession,
  getCheckoutReview,
  getCheckoutSession,
} from '../../api'
import {
  canNavigateToStep,
  checkoutAddressToForm,
  resolveInitialStepFromSession,
  resolveNextStepFromSession,
  type CheckoutFlowAction,
} from '../CheckoutFlow.types'
import {
  clearActiveCheckoutSessionId,
  clearLegacyCheckoutSessionQueryFromUrl,
  parseCheckoutStepFromSearch,
  readActiveCheckoutSessionId,
  replaceCheckoutStepInUrl,
  writeActiveCheckoutSessionId,
} from '../checkoutUrlState'
import { classifyBootstrapError, isExpiredCheckoutError } from './errors'
import { readLegacySessionIdFromSearch, resolveStepFromPopstate } from './navigation'

export const bootstrapCheckoutFlow = async ({
  cartId,
  token,
  dispatch,
  isCancelled,
  setReviewLoadedSessionId,
  onCheckoutCompleted,
}: {
  cartId: string | null
  token: string | null
  dispatch: (action: CheckoutFlowAction) => void
  isCancelled: () => boolean
  setReviewLoadedSessionId: (id: string) => void
  onCheckoutCompleted: (consumedCartId: string) => Promise<void>
}) => {
  const legacySessionId = readLegacySessionIdFromSearch()
  const storedSessionId = readActiveCheckoutSessionId()
  const existingSessionId = storedSessionId ?? legacySessionId
  const hasExistingSession = Boolean(existingSessionId)

  try {
    const resolveCheckoutCartIdForSessionCreation = async (): Promise<string> => {
      const pendingGuestCartId = readCheckoutGuestCartId()
      const preferredCartId = pendingGuestCartId ?? readStoredCartId() ?? cartId
      const resolvedCart = await resolveCart({
        cartId: preferredCartId ?? undefined,
        token: token ?? undefined,
      })

      writeStoredCartId(resolvedCart.cartId)
      if (pendingGuestCartId) {
        writeCheckoutGuestCartId(null)
      }

      const itemCount = Number.isFinite(resolvedCart.itemCount)
        ? resolvedCart.itemCount
        : resolvedCart.items.length
      if (itemCount <= 0) {
        throw new CheckoutApiError(409, 'Carrello vuoto: impossibile creare ordine')
      }

      return resolvedCart.cartId
    }

    let session
    if (existingSessionId) {
      try {
        session = await getCheckoutSession({ token, id: existingSessionId })
        if (session.status === 'completed' || session.currentStep === 'completed') {
          clearActiveCheckoutSessionId()
          await onCheckoutCompleted(session.cartId)
          const resolvedCartId = await resolveCheckoutCartIdForSessionCreation()
          session = await createCheckoutSession({
            token,
            cartId: resolvedCartId,
            successPath: '/dashboard/shop-orders',
            cancelPath: '/checkout',
          })
        }
      } catch (error) {
        if (!isExpiredCheckoutError(error)) throw error
        clearActiveCheckoutSessionId()
        const resolvedCartId = await resolveCheckoutCartIdForSessionCreation()
        session = await createCheckoutSession({
          token,
          cartId: resolvedCartId,
          successPath: '/dashboard/shop-orders',
          cancelPath: '/checkout',
        })
      }
    } else {
      const resolvedCartId = await resolveCheckoutCartIdForSessionCreation()
      session = await createCheckoutSession({
        token,
        cartId: resolvedCartId,
        successPath: '/dashboard/shop-orders',
        cancelPath: '/checkout',
      })
    }

    if (isCancelled()) return
    writeActiveCheckoutSessionId(session.id)
    clearLegacyCheckoutSessionQueryFromUrl()
    if (readCheckoutGuestCartId()) {
      writeCheckoutGuestCartId(null)
    }

    const serverStep = resolveInitialStepFromSession(session)
    const requestedStep =
      typeof window !== 'undefined' ? parseCheckoutStepFromSearch(window.location.search) : null
    const resolvedStep =
      requestedStep && canNavigateToStep(session, requestedStep) ? requestedStep : serverStep
    replaceCheckoutStepInUrl(resolvedStep)

    dispatch({
      type: 'BOOTSTRAP_SUCCEEDED',
      session,
      shippingAddressForm: checkoutAddressToForm(session.shippingAddress),
      billingAddressForm: checkoutAddressToForm(session.billingAddress),
      selectedShippingMethod: session.shippingMethod?.code ?? 'standard',
      selectedPaymentProvider: session.selectedPaymentProvider ?? 'bank_transfer',
      step: resolvedStep,
    })

    if (session.currentStep === 'review') {
      const reviewPayload = await getCheckoutReview({ token, id: session.id })
      if (isCancelled()) return
      session = reviewPayload.session
      dispatch({
        type: 'SESSION_UPDATED',
        session,
        shippingAddressForm: checkoutAddressToForm(session.shippingAddress),
        billingAddressForm: checkoutAddressToForm(session.billingAddress),
        selectedShippingMethod: session.shippingMethod?.code ?? 'standard',
        selectedPaymentProvider:
          session.selectedPaymentProvider
          ?? reviewPayload.defaultPaymentProvider
          ?? 'bank_transfer',
        step: resolveStepFromPopstate({
          session,
          fallbackStep: resolveNextStepFromSession(session),
        }),
      })
      dispatch({
        type: 'REVIEW_UPDATED',
        review: reviewPayload.review,
        shippingMethods: reviewPayload.shippingMethods,
        paymentProviders: reviewPayload.paymentProviders ?? [],
        defaultPaymentProvider: reviewPayload.defaultPaymentProvider ?? 'bank_transfer',
      })
      setReviewLoadedSessionId(session.id)
    }
  } catch (error) {
    if (isCancelled()) return
    if (isExpiredCheckoutError(error)) {
      clearActiveCheckoutSessionId()
    }
    const mapped = classifyBootstrapError({ error, hasExistingSession })
    dispatch({ type: 'BOOTSTRAP_FAILED', code: mapped.code, detail: mapped.detail })
  }
}
